import React, { createContext, useContext, useState } from 'react';

const KYCContext = createContext();

export const useKYC = () => {
  const context = useContext(KYCContext);
  if (!context) {
    throw new Error('useKYC must be used within a KYCProvider');
  }
  return context;
  
};

export const KYCProvider = ({ children }) => {
  const [kycStatus, setKYCStatus] = useState({
    isVerified: false,
    status: 'not_started', // not_started, in_progress, under_review, verified, rejected
    step: 1,
    data: {
      identity: {
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        nationality: '',
        idType: '',
        idNumber: '',
        idDocument: null,
        selfieDocument: null
      },
      address: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        proofOfAddress: null
      },
      sugarDelivery: {
        companyName: '',
        businessType: '',
        deliveryAddress: '',
        storageCapacity: '',
        handlingExperience: '',
        regulatoryLicense: null,
        insuranceDocument: null,
        facilityDocument: null
      }
    },
    submittedAt: null,
    reviewedAt: null
  });

  const updateKYCData = (step, data) => {
    setKYCStatus(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [step]: {
          ...prev.data[step],
          ...data
        }
      }
    }));
  };

  const updateKYCStatus = (newStatus) => {
    setKYCStatus(prev => ({
      ...prev,
      ...newStatus,
      ...(newStatus.status === 'under_review' && { submittedAt: new Date().toISOString() }),
      ...(newStatus.status === 'verified' && { reviewedAt: new Date().toISOString(), isVerified: true })
    }));
  };

  const resetKYC = () => {
    setKYCStatus({
      isVerified: false,
      status: 'not_started',
      step: 1,
      data: {
        identity: {},
        address: {},
        sugarDelivery: {}
      },
      submittedAt: null,
      reviewedAt: null
    });
  };

  const canAccessPhysicalDelivery = () => {
    return kycStatus.isVerified && kycStatus.status === 'verified';
  };

  const getKYCProgress = () => {
    const steps = ['identity', 'address', 'sugarDelivery'];
    let completedSteps = 0;
    
    steps.forEach(step => {
      const stepData = kycStatus.data[step];
      const requiredFields = getRequiredFields(step);
      const completedFields = requiredFields.filter(field => stepData[field]);
      if (completedFields.length === requiredFields.length) {
        completedSteps++;
      }
    });
    
    return (completedSteps / steps.length) * 100;
  };

  const getRequiredFields = (step) => {
    switch (step) {
      case 'identity':
        return ['firstName', 'lastName', 'dateOfBirth', 'nationality', 'idType', 'idNumber'];
      case 'address':
        return ['street', 'city', 'state', 'postalCode', 'country'];
      case 'sugarDelivery':
        return ['companyName', 'businessType', 'deliveryAddress', 'storageCapacity', 'handlingExperience'];
      default:
        return [];
    }
  };

  const value = {
    kycStatus,
    updateKYCData,
    updateKYCStatus,
    resetKYC,
    canAccessPhysicalDelivery,
    getKYCProgress,
    getRequiredFields
  };

  return (
    <KYCContext.Provider value={value}>
      {children}
    </KYCContext.Provider>
  );
};

export default KYCContext; 